/**
 * ColorPicker.ts - Color and font preference management for WTools Mac
 *
 * Since Office JS API cannot auto-detect theme colors/fonts from the master template,
 * users must manually configure their default colors and fonts. These preferences
 * are stored in localStorage for persistence across sessions.
 */

// LocalStorage keys
const STORAGE_KEYS = {
  FILL_COLOR: 'wtools_fill_color',
  TEXT_COLOR: 'wtools_text_color',
  DEFAULT_FONT: 'wtools_default_font'
};

// Default values (Fluent UI blue and black)
const DEFAULTS = {
  FILL_COLOR: '#4472C4',
  TEXT_COLOR: '#000000',
  DEFAULT_FONT: 'Calibri'
};

// ============================================
// Fill Color Functions
// ============================================

/**
 * Gets the current fill color preference.
 * @returns The fill color as a hex string (e.g., "#4472C4")
 */
export function getCurrentFillColor(): string {
  const stored = localStorage.getItem(STORAGE_KEYS.FILL_COLOR);
  return stored || DEFAULTS.FILL_COLOR;
}

/**
 * Sets the current fill color preference.
 * @param color - The fill color as a hex string (e.g., "#4472C4")
 */
export function setCurrentFillColor(color: string): void {
  // Validate hex color format
  if (isValidHexColor(color)) {
    localStorage.setItem(STORAGE_KEYS.FILL_COLOR, color.toUpperCase());
  } else {
    console.warn(`Invalid fill color format: ${color}. Expected hex format like #RRGGBB`);
  }
}

// ============================================
// Text Color Functions
// ============================================

/**
 * Gets the current text color preference.
 * @returns The text color as a hex string (e.g., "#000000")
 */
export function getCurrentTextColor(): string {
  const stored = localStorage.getItem(STORAGE_KEYS.TEXT_COLOR);
  return stored || DEFAULTS.TEXT_COLOR;
}

/**
 * Sets the current text color preference.
 * @param color - The text color as a hex string (e.g., "#000000")
 */
export function setCurrentTextColor(color: string): void {
  // Validate hex color format
  if (isValidHexColor(color)) {
    localStorage.setItem(STORAGE_KEYS.TEXT_COLOR, color.toUpperCase());
  } else {
    console.warn(`Invalid text color format: ${color}. Expected hex format like #RRGGBB`);
  }
}

// ============================================
// Font Functions
// ============================================

/**
 * Gets the current default font preference.
 * @returns The font name (e.g., "Calibri", "Arial")
 */
export function getCurrentFont(): string {
  const stored = localStorage.getItem(STORAGE_KEYS.DEFAULT_FONT);
  return stored || DEFAULTS.DEFAULT_FONT;
}

/**
 * Sets the current default font preference.
 * @param font - The font name (e.g., "Calibri", "Arial")
 */
export function setCurrentFont(font: string): void {
  if (font && font.trim().length > 0) {
    localStorage.setItem(STORAGE_KEYS.DEFAULT_FONT, font.trim());
  } else {
    console.warn('Invalid font name: empty or null');
  }
}

// ============================================
// Utility Functions
// ============================================

/**
 * Validates if a string is a valid hex color format.
 * @param color - The color string to validate
 * @returns true if valid hex color format
 */
function isValidHexColor(color: string): boolean {
  // Match #RGB or #RRGGBB format
  const hexPattern = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
  return hexPattern.test(color);
}

/**
 * Converts a 3-digit hex color to 6-digit format.
 * @param color - The color in #RGB or #RRGGBB format
 * @returns The color in #RRGGBB format
 */
export function normalizeHexColor(color: string): string {
  if (!isValidHexColor(color)) {
    return DEFAULTS.FILL_COLOR;
  }

  // If 3-digit format (#RGB), expand to 6-digit (#RRGGBB)
  if (color.length === 4) {
    const r = color[1];
    const g = color[2];
    const b = color[3];
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }

  return color.toUpperCase();
}

/**
 * Resets all color/font preferences to defaults.
 */
export function resetToDefaults(): void {
  localStorage.setItem(STORAGE_KEYS.FILL_COLOR, DEFAULTS.FILL_COLOR);
  localStorage.setItem(STORAGE_KEYS.TEXT_COLOR, DEFAULTS.TEXT_COLOR);
  localStorage.setItem(STORAGE_KEYS.DEFAULT_FONT, DEFAULTS.DEFAULT_FONT);
}

/**
 * Gets all current preferences as an object.
 * @returns Object containing all color/font preferences
 */
export function getAllPreferences(): {
  fillColor: string;
  textColor: string;
  defaultFont: string;
} {
  return {
    fillColor: getCurrentFillColor(),
    textColor: getCurrentTextColor(),
    defaultFont: getCurrentFont()
  };
}

/**
 * Sets all preferences at once.
 * @param preferences - Object containing preferences to set
 */
export function setAllPreferences(preferences: {
  fillColor?: string;
  textColor?: string;
  defaultFont?: string;
}): void {
  if (preferences.fillColor) {
    setCurrentFillColor(preferences.fillColor);
  }
  if (preferences.textColor) {
    setCurrentTextColor(preferences.textColor);
  }
  if (preferences.defaultFont) {
    setCurrentFont(preferences.defaultFont);
  }
}
